import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Truck, Plus, Users, MessageSquare, FileText,
  LogOut, RefreshCw, Mail as MailIcon, BarChart3, Calendar, UserCog, Newspaper, Shield, TrendingUp, MessageCircle, Beaker, Upload
} from 'lucide-react';
import { LoadsTab } from '@/components/dashboard/LoadsTab';
import { QuotesTab, ContactsTab, ApplicationsTab } from '@/components/dashboard/DataTabs';
import { LoadFormModal } from '@/components/dashboard/LoadFormModal';
import { CarriersTab } from '@/components/dashboard/CarriersTab';
import { NotifyCarriersModal, SendDigestModal } from '@/components/dashboard/EmailNotifications';
import { EmailAnalyticsTab } from '@/components/dashboard/EmailAnalyticsTab';
import { DigestSettingsTab } from '@/components/dashboard/DigestSettingsTab';
import { DispatchersTab } from '@/components/dashboard/DispatchersTab';
import { BlogTab } from '@/components/dashboard/BlogTab';
import { PermissionsTab } from '@/components/dashboard/PermissionsTab';
import { MarketIntelTab } from '@/components/dashboard/MarketIntelTab';
import { PostingsTab } from '@/components/dashboard/PostingsTab';
import { EngagementTab } from '@/components/dashboard/EngagementTab';
import { LiveChatsTab } from '@/components/dashboard/LiveChatsTab';
import { EmailLabTab } from '@/components/dashboard/EmailLabTab';
import { ProfilePopover } from '@/components/dashboard/ProfilePopover';
import { PhoneOnboardingBanner } from '@/components/dashboard/PhoneOnboardingBanner';
import { ChatNotificationBell } from '@/components/dashboard/ChatNotificationBell';
import { BulkLoadUploadModal } from '@/components/dashboard/BulkLoadUploadModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAB_META = {
  loads:        { label: 'My Loads',        icon: Truck },
  dispatchers:  { label: 'Dispatchers',     icon: UserCog },
  carriers:     { label: 'Carriers',        icon: MailIcon },
  digest:       { label: 'Auto Digest',     icon: Calendar },
  analytics:    { label: 'Email Analytics', icon: BarChart3 },
  blog:         { label: 'Blog',            icon: Newspaper },
  market_intel: { label: 'Market Intel',    icon: TrendingUp },
  postings:     { label: 'Postings',        icon: BarChart3 },
  engagement:   { label: 'Engagement',      icon: MailIcon },
  live_chats:   { label: 'Live Chats',      icon: MessageCircle },
  email_lab:    { label: 'Email Lab',       icon: Beaker },
  quotes:       { label: 'Quotes',          icon: FileText },
  contacts:     { label: 'Contacts',        icon: MessageSquare },
  applications: { label: 'Applications',    icon: Users },
  permissions:  { label: 'Permissions',     icon: Shield },
};

const SELF_MANAGED_TABS = ['carriers', 'digest', 'dispatchers', 'blog', 'permissions', 'market_intel', 'postings', 'engagement', 'live_chats', 'email_lab'];

// Tab → component mapping for self-managed (zero-prop) tabs
const STANDALONE_TABS = {
  carriers:     (ctx) => <CarriersTab isAdmin={ctx.canManageCarriers} />,
  digest:       () => <DigestSettingsTab />,
  analytics:    () => <EmailAnalyticsTab />,
  dispatchers:  () => <DispatchersTab />,
  blog:         () => <BlogTab />,
  market_intel: () => <MarketIntelTab />,
  postings:     () => <PostingsTab />,
  engagement:   () => <EngagementTab />,
  live_chats:   () => <LiveChatsTab />,
  email_lab:    () => <EmailLabTab />,
  permissions:  () => <PermissionsTab />,
};

const renderActiveTab = (ctx) => {
  // Self-managed tabs render their own loading state
  const standalone = STANDALONE_TABS[ctx.activeTab];
  if (standalone) return standalone(ctx);

  // Data-driven tabs share the page-level loading spinner
  if (ctx.loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }
  const { activeTab, myLoads, quotes, contacts, applications,
    canPostLoads, canManageAllLoads, canSendDigest,
    setEditingLoad, handleDeleteLoad, handleBulkDelete,
    setShowCreateModal, setNotifyLoad } = ctx;
  if (activeTab === 'loads') {
    return (
      <LoadsTab
        loads={myLoads}
        onEdit={(canPostLoads || canManageAllLoads) ? setEditingLoad : undefined}
        onDelete={(canPostLoads || canManageAllLoads) ? handleDeleteLoad : undefined}
        onBulkDelete={(canPostLoads || canManageAllLoads) ? handleBulkDelete : undefined}
        onCreateNew={canPostLoads ? () => setShowCreateModal(true) : undefined}
        onNotify={canSendDigest ? setNotifyLoad : undefined}
        canManageAll={canManageAllLoads}
      />
    );
  }
  if (activeTab === 'quotes')       return <QuotesTab quotes={quotes} />;
  if (activeTab === 'contacts')     return <ContactsTab contacts={contacts} />;
  if (activeTab === 'applications') return <ApplicationsTab applications={applications} />;
  return null;
};

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('loads');
  const [myLoads, setMyLoads] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingLoad, setEditingLoad] = useState(null);
  const [notifyLoad, setNotifyLoad] = useState(null);
  const [showDigestModal, setShowDigestModal] = useState(false);
  const [perms, setPerms] = useState(null);
  const [chatUnread, setChatUnread] = useState(0);

  const isAdmin = user?.role === 'admin';

  // Fetch permissions on mount
  useEffect(() => {
    axios.get(`${API}/permissions/my`)
      .then(res => setPerms(res.data.permissions))
      .catch(() => {
        // Fallback: show loads only
        setPerms({ tabs: ['loads'], can_post_loads: true, can_send_digest: false, can_manage_carriers: false, can_manage_users: false });
      });
  }, []);

  // Poll unread chat count every 8s for the sidebar badge (only when user has Live Chats access)
  useEffect(() => {
    if (!perms?.tabs?.includes('live_chats')) return;
    const tick = () => {
      axios.get(`${API}/chat/unread-count`)
        .then((r) => setChatUnread(r.data?.unread_count || 0))
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 8000);
    return () => clearInterval(id);
  }, [perms]);

  const allowedTabs = perms?.tabs || ['loads'];
  const canPostLoads = perms?.can_post_loads ?? true;
  const canSendDigest = perms?.can_send_digest ?? false;
  const canManageCarriers = perms?.can_manage_carriers ?? false;
  const canManageAllLoads = perms?.can_manage_all_loads ?? false;

  const fetchData = useCallback(async () => {
    if (SELF_MANAGED_TABS.includes(activeTab)) return;
    setLoading(true);
    try {
      if (activeTab === 'loads') {
        const response = await axios.get(`${API}/my-loads`);
        setMyLoads(response.data);
      } else if (activeTab === 'quotes') {
        const response = await axios.get(`${API}/quotes`);
        setQuotes(response.data);
      } else if (activeTab === 'contacts') {
        const response = await axios.get(`${API}/contacts`);
        setContacts(response.data);
      } else if (activeTab === 'applications') {
        const response = await axios.get(`${API}/careers/applications`);
        setApplications(response.data);
      }
    } catch {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDeleteLoad = async (loadId) => {
    if (!window.confirm('Are you sure you want to delete this load?')) return;
    try {
      await axios.delete(`${API}/loads/${loadId}`);
      toast.success('Load deleted successfully');
      fetchData();
    } catch {
      toast.error('Failed to delete load');
    }
  };

  const handleBulkDelete = async (ids) => {
    try {
      const res = await axios.post(`${API}/loads/bulk-delete`, { ids });
      const { deleted = 0, skipped = 0 } = res.data || {};
      if (skipped > 0) {
        toast.warning(`Deleted ${deleted} · ${skipped} skipped (not authorized)`);
      } else {
        toast.success(`Deleted ${deleted} load${deleted === 1 ? '' : 's'}`);
      }
      fetchData();
    } catch {
      toast.error('Failed to delete selected loads');
    }
  };

  // Build visible tabs from permissions
  const tabs = allowedTabs
    .filter(id => TAB_META[id])
    .map(id => ({
      id,
      label: id === 'loads' && canManageAllLoads ? 'All Loads' : TAB_META[id].label,
      icon: TAB_META[id].icon,
      count: id === 'loads' ? myLoads.length
        : id === 'quotes' ? quotes.length
        : id === 'contacts' ? contacts.length
        : id === 'applications' ? applications.length
        : null,
    }));

  const handleModalClose = () => {
    setShowCreateModal(false);
    setEditingLoad(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    fetchData();
  };

  const roleBadge = user?.role ? (
    <span className="ml-2 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-bold uppercase rounded">
      {user.role}
    </span>
  ) : null;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-zinc-900 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl font-black">Dispatcher Dashboard</h1>
              <p className="text-zinc-400 text-sm mt-1">
                Welcome back, <span className="text-white font-medium">{user?.name || user?.email}</span>
                {roleBadge}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {canSendDigest && (
                <button
                  onClick={() => setShowDigestModal(true)}
                  className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 font-bold uppercase tracking-wide text-sm transition-colors"
                  data-testid="send-digest-header-btn"
                >
                  <MailIcon className="w-4 h-4" />
                  Send Digest
                </button>
              )}
              {canPostLoads && (
                <>
                  <button
                    onClick={() => setShowBulkModal(true)}
                    className="flex items-center gap-2 border border-zinc-600 hover:border-white text-zinc-200 hover:text-white px-3 py-2 font-bold uppercase tracking-wide text-sm transition-colors"
                    data-testid="bulk-upload-btn"
                  >
                    <Upload className="w-4 h-4" />
                    Bulk Upload
                  </button>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 font-bold uppercase tracking-wide text-sm transition-colors"
                    data-testid="create-load-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Post Load
                  </button>
                </>
              )}
              <ProfilePopover />
              {perms?.tabs?.includes('live_chats') && (
                <ChatNotificationBell onNavigateToChats={(sessionId) => {
                  setActiveTab('live_chats');
                  if (sessionId && typeof window !== 'undefined') {
                    window.sessionStorage.setItem('live-chats-open-session', sessionId);
                  }
                }} />
              )}
              <button
                onClick={logout}
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <PhoneOnboardingBanner />

      {/* Tabs */}
      <div className="bg-white border-b border-zinc-200 sticky top-16 lg:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-semibold uppercase tracking-wide border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-red-600 text-red-600'
                    : 'border-transparent text-zinc-500 hover:text-zinc-900'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.id === 'live_chats' && chatUnread > 0 && (
                  <span className="bg-red-600 text-white px-1.5 py-0.5 text-[10px] font-bold rounded-full" data-testid="live-chats-unread-badge">
                    {chatUnread}
                  </span>
                )}
                {tab.count !== null && (
                  <span className="bg-zinc-100 text-zinc-600 px-2 py-0.5 text-xs rounded">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderActiveTab({
          activeTab, loading,
          myLoads, quotes, contacts, applications,
          canManageCarriers, canPostLoads, canManageAllLoads, canSendDigest,
          setEditingLoad, handleDeleteLoad, handleBulkDelete,
          setShowCreateModal, setNotifyLoad,
        })}
      </div>

      {(canPostLoads || canManageAllLoads) && (showCreateModal || editingLoad) && (
        <LoadFormModal
          load={editingLoad}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          canManageAll={canManageAllLoads}
        />
      )}

      {canPostLoads && showBulkModal && (
        <BulkLoadUploadModal
          onClose={() => setShowBulkModal(false)}
          onSuccess={() => { setShowBulkModal(false); fetchData(); }}
        />
      )}

      {notifyLoad && (
        <NotifyCarriersModal
          load={notifyLoad}
          onClose={() => setNotifyLoad(null)}
        />
      )}

      {showDigestModal && (
        <SendDigestModal onClose={() => setShowDigestModal(false)} />
      )}
    </div>
  );
};

export default DashboardPage;
